// Kelola template desain faktur & struk (brief bos #18-19) — CRUD template
// per jenis dokumen + cabang, dengan pratinjau hidup di samping form supaya
// owner bisa "mendesain sambil melihat".
import { useEffect, useMemo, useState } from "react";
import PesanWaEditor from "../components/faktur/PesanWaEditor";
import WarrantySettings from "../components/faktur/WarrantySettings";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  FileText, Plus, Pencil, Trash2, Star, Info, X, ShieldAlert,
  Upload, Image as ImageIcon, Loader2,
} from "lucide-react";
import FakturPrint, { isStruk } from "../components/faktur/FakturPrint";
import StrukPrint from "../components/faktur/StrukPrint";
import LoadingState from "@/components/ui/loading-state";

const JENIS_LABEL = {
  penjualan: "Faktur Penjualan",
  pindah_gudang: "Surat Jalan Pindah Gudang",
  retur_penjualan: "Retur Penjualan",
  retur_pembelian: "Retur Pembelian",
  pembelian: "Faktur Pembelian",
  struk: "Struk Kasir (Thermal)",
  adjustment: "Berita Acara Penyesuaian Stok",
};

// Jenis dokumen yang benar-benar terikat SATU gudang (lihat routers/faktur.py
// _pilih_template dipanggil dengan warehouse_id) — dipakai untuk keterangan di
// blok "Pembatasan Gudang" supaya owner tidak bingung kenapa aturan itu tidak
// berpengaruh untuk faktur penjualan/retur penjualan/struk.
const JENIS_TERIKAT_GUDANG = ["pembelian", "pindah_gudang", "retur_pembelian", "adjustment"];

// Aturan orientasi cetak. Sejak brief owner 30 Agu 2026 poin 4, DAFTARNYA
// datang dari backend (GET /faktur/templates -> orientasi_terkunci &
// orientasi_bawaan) supaya tidak ada dua salinan aturan yang bisa berpisah
// jalan. Yang di bawah ini hanya CADANGAN kalau backend masih versi lama —
// nilainya sengaja sama dengan bawaan backend sekarang.
const ORIENTASI_TERKUNCI_CADANGAN = { thermal_58: "portrait", thermal_80: "portrait" };
const ORIENTASI_BAWAAN_CADANGAN = {
  continuous_half: "landscape", thermal_58: "portrait", thermal_80: "portrait",
};

// Kenapa sebuah ukuran kertas orientasinya dikunci — ditampilkan apa adanya
// ke owner, bukan disembunyikan, supaya jelas ini bukan pilihan yang hilang.
const ALASAN_ORIENTASI_TERKUNCI = {
  thermal_58: "struk thermal dicetak di kertas gulung dengan tinggi mengikuti isi — orientasi tidak berpengaruh sama sekali",
  thermal_80: "struk thermal dicetak di kertas gulung dengan tinggi mengikuti isi — orientasi tidak berpengaruh sama sekali",
};

const ORIENTASI_LABEL = { portrait: "Portrait (tegak)", landscape: "Landscape (mendatar)" };

// Batas & bawaan skala cetak — cadangan kalau backend versi lama tidak
// mengirim skala_cetak_min/maks. SAMA dengan routers/faktur.py.
const SKALA_MIN_CADANGAN = 50;
const SKALA_MAKS_CADANGAN = 150;
const SKALA_DEFAULT = 100;

// Loncatan cepat yang paling sering dipakai saat mengalibrasi printer
// continuous — mengetik angka tetap bisa, ini cuma jalan pintas.
const SKALA_PRESET = [70, 80, 90, 100, 110, 125];

// Batas ukuran logo — SAMA dengan MAKS_LOGO_BASE64_CHARS backend
// (routers/faktur.py), dihitung dari karakter base64 SETELAH prefix
// "data:...;base64," dibuang. TARGET dipasang sedikit di bawah batas keras
// supaya ada margin aman (prefix data URL, pembulatan canvas, dll).
const MAKS_LOGO_BASE64_CHARS = 500_000;
const TARGET_LOGO_BASE64_CHARS = 450_000;

function base64Dari(dataUrl) {
  return (dataUrl || "").split(",")[1] || "";
}

/** Ubah file gambar jadi data URL base64 yang aman dikirim ke backend —
 *  diperkecil (resize) & dikompres (kualitas JPEG diturunkan bertahap) DI
 *  BROWSER dulu supaya foto langsung dari HP (bisa 5MB+) tidak menabrak error
 *  400 backend tanpa penjelasan. SVG (vektor, biasanya sudah kecil) dilewatkan
 *  apa adanya tanpa digambar ulang lewat canvas. */
async function kompresGambarLogo(file) {
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("Berkas yang dipilih bukan gambar. Pilih file PNG, JPG, atau SVG.");
  }
  if (file.size > 15 * 1024 * 1024) {
    throw new Error("Ukuran file terlalu besar untuk diproses (maksimal 15MB). Pilih foto lain yang lebih kecil.");
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Gagal membaca berkas gambar."));
    reader.readAsDataURL(file);
  });

  if (file.type === "image/svg+xml") {
    if (base64Dari(dataUrl).length > MAKS_LOGO_BASE64_CHARS) {
      throw new Error("Berkas SVG ini terlalu besar. Sederhanakan gambarnya atau pakai format PNG/JPG.");
    }
    return dataUrl;
  }

  const img = await new Promise((resolve, reject) => {
    const im = new window.Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("Gagal memuat gambar ini — coba format lain (PNG/JPG)."));
    im.src = dataUrl;
  });

  // Dicoba dari resolusi & kualitas paling bagus, turun bertahap sampai
  // ketemu kombinasi yang muat di bawah TARGET_LOGO_BASE64_CHARS.
  const dimensiDicoba = [900, 700, 540, 420, 320];
  const kualitasDicoba = [0.85, 0.72, 0.6, 0.48, 0.38];
  for (const maxDim of dimensiDicoba) {
    const skala = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * skala));
    const h = Math.max(1, Math.round(img.height * skala));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // JPEG tidak mendukung transparan — latar putih supaya logo tetap rapi
    // dicetak di atas kertas putih.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    for (const q of kualitasDicoba) {
      const hasil = canvas.toDataURL("image/jpeg", q);
      if (base64Dari(hasil).length <= TARGET_LOGO_BASE64_CHARS) {
        return hasil;
      }
    }
  }
  throw new Error(
    "Gambar ini tidak bisa diperkecil sampai di bawah batas ukuran (±375KB). Coba foto lain yang lebih sederhana."
  );
}

const UKURAN_LABEL = {
  A4: "A4 (210×297mm)",
  A5: "A5 (148×210mm)",
  continuous_half: "Continuous Half (±210×140mm)",
  thermal_58: "Thermal 58mm",
  thermal_80: "Thermal 80mm",
};

const blankForm = (jenisDefault = "penjualan", ukuranDefault = "A4", orientasiBawaan = ORIENTASI_BAWAAN_CADANGAN) => ({
  id: null,
  jenis: jenisDefault,
  nama: "",
  cabang: "",
  ukuran_kertas: ukuranDefault,
  // Kosong dibiarkan kosong: backend yang menentukan bawaannya per ukuran
  // kertas (satu sumber). Diisi di sini hanya supaya select-nya tidak
  // "uncontrolled" sebelum owner menyentuhnya.
  orientasi: orientasiBawaan[ukuranDefault] || "portrait",
  skala_cetak: SKALA_DEFAULT,
  fit_otomatis: false,
  kop: { judul: "", alamat: "", telepon: "", tampil_logo: true, logo_url: "" },
  footer_teks: "",
  pesan_wa: "",
  catatan_penjual: "",
  syarat_ketentuan: "",
  opsi: {
    tampil_sku: true, tampil_imei: true, tampil_ttd: false, tampil_catatan: true, sertakan_link_csat: true,
    // Kontrak B4 (brief 23 Agu poin 10) — bawaan TAMPIL, sama dengan default
    // backend TemplateOpsi.tampil_komponen_bundle = True. Dipertahankan saat
    // blankForm versi ZOA (orientasi & skala cetak) menggantikan versi lama.
    tampil_komponen_bundle: true,
  },
  is_default: false,
  all_warehouses: true,
  allowed_warehouse_ids: [],
});

const inp = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200";

// Data contoh statis untuk pratinjau hidup — TIDAK pernah dikirim ke server,
// hanya supaya owner melihat bentuk faktur/struk sambil mengatur opsinya.
// Nama cabang contoh diturunkan dari nama toko di pengaturan Branding
// (keputusan owner 24 Sep 2026, white-label): dulu tertanam nama toko milik
// pemilik produk LAMA, sehingga pratinjau pembeli produk memamerkan toko orang lain.
function buatDataContoh(namaToko) {
  const cabangContoh = `${namaToko} Pusat`;
  const DUMMY = {
    penjualan: {
      tanggal: "06-08-2026", waktu: "14:20",
      nama_pelanggan: "Budi Santoso", no_hp: "081234567890",
      items: [
        { nama_barang: "iPhone 13 128GB", sku: "HP-IP13-128", imei: "356789012345678", qty: 1, harga: 8500000 },
        { nama_barang: "Tempered Glass", sku: "ACC-TG-001", qty: 2, harga: 25000 },
        // Contoh baris paket bundling KIT (kontrak B4, opsi tampil_komponen_bundle)
        // supaya owner langsung melihat efek checkbox-nya di pratinjau hidup.
        {
          nama_barang: "Paket Hemat A", sku: "KIT-HEMAT-A", qty: 1, harga: 1200000,
          komponen: [
            { nama: "Casing Silikon", qty: 1, sku: "ACC-CS-01" },
            { nama: "iPhone 13 128GB Second", qty: 1, sku: "HP-IP13-128-2ND", imei: "351234567890999" },
          ],
        },
      ],
      payments: [{ method: "Cash", amount: 5000000 }, { method: "Transfer", amount: 3550000 }],
      trade_in: true, trade_in_note: "Tukar tambah iPhone X",
      trade_in_items: [{ nama: "iPhone X 64GB", harga: 2000000, imei: "351234567890123" }],
      catatan: "Unit sudah dicek fungsi & fisik sebelum diserahkan.",
      nama_penjual: "Siti Aminah", branch: cabangContoh,
      total_harga: 8550000,
    },
    pembelian: {
      tanggal: "05-08-2026", supplier_nama: "CV Sumber Ponsel", no_po_supplier: "PO/2026/081",
      warehouse_nama: "Gudang Utama", cabang: cabangContoh,
      items: [
        { sku: "HP-IP13-128", nama: "iPhone 13 128GB", qty: 5, harga_per_unit: 8000000, nilai: 40000000,
          units: [{ identifier: "356789012345678" }, { identifier: "356789012345679" }] },
      ],
      catatan: "Barang diterima lengkap sesuai PO.",
      code: "PO-000123", nilai_total: 40000000,
    },
    pindah_gudang: {
      tanggal: "04-08-2026", code: "TRF-000045",
      dari_warehouse_nama: "Gudang Utama", ke_warehouse_nama: `Toko ${cabangContoh}`,
      items: [{ sku: "HP-IP13-128", nama: "iPhone 13 128GB", qty: 2, nilai: 16000000 }],
      catatan: "Restock toko cabang.",
      nilai_total: 16000000,
    },
  };
  DUMMY.retur_penjualan = DUMMY.penjualan;
  DUMMY.retur_pembelian = DUMMY.pembelian;
  DUMMY.struk = DUMMY.penjualan;
  return DUMMY;
}

export default function FakturSettings() {
  const { can, branding } = useAuth();
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";
  const DUMMY = useMemo(() => buatDataContoh(namaToko), [namaToko]);
  const canView = can("faktur", "view");
  const canCreate = can("faktur", "create");
  const canUpdate = can("faktur", "update");
  const canDelete = can("faktur", "delete");

  const [items, setItems] = useState([]);
  const [jenisOptions, setJenisOptions] = useState([]);
  const [jenisDenganData, setJenisDenganData] = useState([]);
  const [ukuranOptions, setUkuranOptions] = useState([]);
  const [orientasiOptions, setOrientasiOptions] = useState(["portrait", "landscape"]);
  // Aturan orientasi & rentang skala datang dari backend (satu sumber) —
  // lihat ORIENTASI_TERKUNCI_CADANGAN untuk kenapa masih ada nilai cadangan.
  const [orientasiTerkunci, setOrientasiTerkunci] = useState(ORIENTASI_TERKUNCI_CADANGAN);
  const [orientasiBawaan, setOrientasiBawaan] = useState(ORIENTASI_BAWAAN_CADANGAN);
  const [skalaMin, setSkalaMin] = useState(SKALA_MIN_CADANGAN);
  const [skalaMaks, setSkalaMaks] = useState(SKALA_MAKS_CADANGAN);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const [cabangOpsi, setCabangOpsi] = useState([]);
  const [cabangFreeText, setCabangFreeText] = useState(false);

  const [warehouseOpsi, setWarehouseOpsi] = useState([]);
  const [warehouseFreeText, setWarehouseFreeText] = useState(false);

  const [compressingLogo, setCompressingLogo] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/faktur/templates")
      .then(({ data }) => {
        setItems(data.items || []);
        setJenisOptions(data.jenis_options || []);
        setJenisDenganData(data.jenis_dengan_data_cetak || []);
        setUkuranOptions(data.ukuran_kertas_options || []);
        setOrientasiOptions(data.orientasi_options || ["portrait", "landscape"]);
        // `||` (bukan `??`): backend lama tidak mengirim field ini sama
        // sekali -> pakai cadangan; backend baru selalu mengirim isinya.
        setOrientasiTerkunci(data.orientasi_terkunci || ORIENTASI_TERKUNCI_CADANGAN);
        setOrientasiBawaan(data.orientasi_bawaan || ORIENTASI_BAWAAN_CADANGAN);
        setSkalaMin(data.skala_cetak_min || SKALA_MIN_CADANGAN);
        setSkalaMaks(data.skala_cetak_maks || SKALA_MAKS_CADANGAN);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat daftar template faktur"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cabang ditarik dari data yang sudah ada di modul Inventory (pola yang
  // sama dipakai form Gudang di InventoryStok.jsx). Kalau akun ini tidak
  // punya izin melihat gudang (403) atau endpointnya gagal, jatuhkan ke
  // ketikan bebas supaya form tetap bisa dipakai.
  useEffect(() => {
    let batal = false;
    api.get("/inventory/cabang-options")
      .then((r) => { if (!batal) setCabangOpsi(r.data.items || []); })
      .catch(() => { if (!batal) setCabangFreeText(true); });
    return () => { batal = true; };
  }, []);

  // Daftar gudang untuk blok "Pembatasan Gudang" — sumbernya `db.inventory_
  // warehouses` lewat endpoint yang sudah ada (routers/inventory/warehouses.py).
  // Akun tanpa izin "inventory_gudang.view" (403) atau endpoint gagal jatuh ke
  // ketikan bebas (tempel id gudang manual) supaya form tetap bisa dipakai.
  useEffect(() => {
    let batal = false;
    api.get("/inventory/warehouses")
      .then((r) => { if (!batal) setWarehouseOpsi(r.data.items || []); })
      .catch(() => { if (!batal) setWarehouseFreeText(true); });
    return () => { batal = true; };
  }, []);

  const grouped = useMemo(
    () => jenisOptions.map((j) => ({ jenis: j, list: items.filter((it) => it.jenis === j) })),
    [jenisOptions, items]
  );

  const belumAdaDataCetak = (jenis) => jenis !== "struk" && !jenisDenganData.includes(jenis);

  const openTambah = () => setForm(
    blankForm(jenisOptions[0] || "penjualan", ukuranOptions[0] || "A4", orientasiBawaan));
  // `item` datang dari GET /faktur/templates, yang SUDAH dilengkapi backend
  // (_lengkapi_template) — logo_url/orientasi/catatan_penjual/syarat_ketentuan/
  // all_warehouses/allowed_warehouse_ids selalu ada walau dokumen lama. Tetap
  // digabung di atas blankForm sebagai jaga-jaga kalau ada field yang hilang.
  const openEdit = (item) => setForm({
    ...blankForm(item.jenis, item.ukuran_kertas, orientasiBawaan), ...item, cabang: item.cabang || "",
    // Merge (bukan timpa penuh) supaya opsi BARU yang belum ada di template
    // lama (mis. tampil_komponen_bundle, kontrak B4) tetap kebagian default
    // blankForm-nya, alih-alih hilang jadi undefined -> tercentang "off" tak
    // sengaja di form padahal backend defaultnya True.
    opsi: { ...blankForm().opsi, ...(item.opsi || {}) },
  });
  const tutupForm = () => setForm(null);

  const onPilihLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // supaya file yang sama bisa dipilih ulang kalau perlu
    if (!file) return;
    setCompressingLogo(true);
    try {
      const dataUrl = await kompresGambarLogo(file);
      setForm((f) => ({ ...f, kop: { ...f.kop, logo_url: dataUrl } }));
      toast.success("Logo siap dipakai — sudah dikompres otomatis di browser.");
    } catch (err) {
      toast.error(err.message || "Gagal memproses gambar logo.");
    } finally {
      setCompressingLogo(false);
    }
  };
  const hapusLogo = () => setForm((f) => ({ ...f, kop: { ...f.kop, logo_url: "" } }));

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama template wajib diisi"); return; }
    if (!form.all_warehouses && (form.allowed_warehouse_ids || []).filter(Boolean).length === 0) {
      toast.error("Tentukan gudang mana yang boleh memakai template ini, atau centang \"Boleh dipakai semua gudang\".");
      return;
    }
    // Skala diketik bebas di kotak angka — dicegat di sini supaya owner dapat
    // pesan yang jelas, bukan 400 mentah dari backend. Backend tetap menolak
    // sendiri (gerbang sebenarnya ada di sana, bukan di layar ini).
    const skala = Number(form.skala_cetak);
    if (!Number.isFinite(skala) || skala < skalaMin || skala > skalaMaks) {
      toast.error(`Skala cetak harus antara ${skalaMin}% dan ${skalaMaks}%.`);
      return;
    }
    const payload = {
      jenis: form.jenis,
      nama: form.nama.trim(),
      cabang: form.cabang.trim() || null,
      ukuran_kertas: form.ukuran_kertas,
      orientasi: form.orientasi || "",
      skala_cetak: skala,
      fit_otomatis: !!form.fit_otomatis,
      kop: form.kop,
      footer_teks: form.footer_teks || "",
      pesan_wa: form.pesan_wa || "",
      catatan_penjual: form.catatan_penjual || "",
      syarat_ketentuan: form.syarat_ketentuan || "",
      opsi: form.opsi,
      is_default: !!form.is_default,
      all_warehouses: !!form.all_warehouses,
      allowed_warehouse_ids: form.all_warehouses ? [] : (form.allowed_warehouse_ids || []).filter(Boolean),
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/faktur/templates/${form.id}`, payload);
        toast.success(`Template "${payload.nama}" diperbarui`);
      } else {
        await api.post("/faktur/templates", payload);
        toast.success(`Template "${payload.nama}" dibuat`);
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan template faktur");
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (item) => {
    if (!window.confirm(`Hapus template "${item.nama}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/faktur/templates/${item.id}`);
      toast.success(`Template "${item.nama}" dihapus`);
      if (form?.id === item.id) setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus template");
    }
  };

  const jadikanDefault = async (item) => {
    try {
      // PENTING: PUT menggantikan SELURUH dokumen (bukan patch parsial) — kalau
      // field baru (orientasi/skala_cetak/fit_otomatis/catatan_penjual/
      // syarat_ketentuan/all_warehouses/allowed_warehouse_ids) tidak disertakan,
      // backend akan mengembalikannya ke default TemplateReq dan diam-diam
      // MENGHAPUS pengaturan itu dari template yang bersangkutan. `item` sudah
      // lengkap (dari _lengkapi_template backend), jadi semua field disertakan
      // apa adanya. SETIAP field baru di TemplateReq WAJIB ditambahkan di sini
      // juga — kalau lupa, "Jadikan Default" akan mereset diam-diam.
      await api.put(`/faktur/templates/${item.id}`, {
        jenis: item.jenis, nama: item.nama, cabang: item.cabang || null,
        ukuran_kertas: item.ukuran_kertas, orientasi: item.orientasi || "",
        skala_cetak: Number(item.skala_cetak) || SKALA_DEFAULT,
        fit_otomatis: !!item.fit_otomatis,
        kop: item.kop, footer_teks: item.footer_teks || "",
        catatan_penjual: item.catatan_penjual || "", syarat_ketentuan: item.syarat_ketentuan || "",
        pesan_wa: item.pesan_wa || "",
        opsi: item.opsi, is_default: true,
        all_warehouses: item.all_warehouses !== false,
        allowed_warehouse_ids: item.allowed_warehouse_ids || [],
      });
      toast.success(`"${item.nama}" dijadikan default`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjadikan template ini default");
    }
  };

  const dummyEntity = form ? (DUMMY[form.jenis] || DUMMY.penjualan) : null;
  // JUJUR SOAL PRATINJAU: tautan/QR CSAT di bawah ini CONTOH STATIS, bukan
  // undangan sungguhan. GET /faktur/data/penjualan (dipakai cetak asli) hanya
  // menampilkan undangan CSAT yang SUDAH pernah dibuat lewat "Kirim Faktur via
  // WhatsApp" / "Buat Tautan CSAT" — kalau belum pernah dibuat untuk transaksi
  // itu, blok ini TIDAK akan tercetak sama sekali walau opsi dicentang. Domain
  // ".invalid" (RFC 2606) dipakai sengaja supaya tidak terlihat seperti tautan
  // asli yang bisa dibuka.
  const dummyCsat = form?.opsi?.sertakan_link_csat
    ? { survey_url: "https://contoh.pratinjau.invalid/csat/PRATINJAU-BUKAN-TAUTAN-ASLI", status: "terkirim", sudah_diisi: false, kedaluwarsa: false }
    : null;
  const KomponenPreview = form && isStruk(form) ? StrukPrint : FakturPrint;

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Pengaturan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" /> Template Faktur &amp; Struk
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Atur tampilan faktur, surat jalan, dan struk kasir per jenis dokumen dan cabang. Satu template bisa
            dijadikan default supaya otomatis dipakai saat mencetak.
          </p>
        </div>
        {canView && canCreate && !form && (
          <button onClick={openTambah} data-testid="ft-tambah"
            className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Tambah Template
          </button>
        )}
      </header>

      {!canView ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="ft-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Template Faktur &amp; Struk</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin melihat modul ini.</p>
        </div>
      ) : (
        <>
          {!form && <WarrantySettings canUpdate={canUpdate} />}
          {form && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Form */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm no-print">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-semibold text-slate-900">
                    {form.id ? `Ubah — ${form.nama || "Template"}` : "Template Baru"}
                  </h3>
                  <button onClick={tutupForm} data-testid="ft-tutup-form" className="text-slate-400 hover:text-slate-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Jenis Dokumen</span>
                      <select value={form.jenis} data-testid="ft-jenis"
                        onChange={(e) => setForm((f) => ({ ...f, jenis: e.target.value }))}
                        className={inp}>
                        {jenisOptions.map((j) => <option key={j} value={j}>{JENIS_LABEL[j] || j}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Ukuran Kertas</span>
                      <select value={form.ukuran_kertas} data-testid="ft-ukuran"
                        onChange={(e) => {
                          const ukuranBaru = e.target.value;
                          setForm((f) => {
                            // Ukuran terkunci -> ikut kuncinya. Ukuran yang
                            // punya bawaan sendiri (continuous) -> mulai dari
                            // bawaannya, supaya pindah ke continuous tidak
                            // diam-diam mewarisi portrait dari A4. A4/A5 tidak
                            // punya bawaan khusus -> pilihan owner dipertahankan
                            // (perilaku lama saat pindah A4 <-> A5).
                            const kunci = orientasiTerkunci[ukuranBaru];
                            return {
                              ...f,
                              ukuran_kertas: ukuranBaru,
                              orientasi: kunci || orientasiBawaan[ukuranBaru] || f.orientasi || "portrait",
                            };
                          });
                        }}
                        className={inp}>
                        {ukuranOptions.map((u) => <option key={u} value={u}>{UKURAN_LABEL[u] || u}</option>)}
                      </select>
                    </label>
                  </div>

                  {/* Orientasi cetak — A4/A5/continuous bebas dipilih (owner
                      30 Agu 2026: cetakan continuous "tidak sesuai kertas" dan
                      dulu tidak ada jalan keluarnya dari UI). Thermal masih
                      terkunci, dan alasannya ditampilkan apa adanya, bukan
                      disembunyikan. Daftar kuncinya datang dari backend. */}
                  {orientasiTerkunci[form.ukuran_kertas] ? (
                    <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600"
                         data-testid="ft-orientasi-terkunci">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                      <span>
                        Orientasi cetak:{" "}
                        <b className="text-slate-800">
                          {ORIENTASI_LABEL[orientasiTerkunci[form.ukuran_kertas]] || orientasiTerkunci[form.ukuran_kertas]}
                        </b>
                        {ALASAN_ORIENTASI_TERKUNCI[form.ukuran_kertas]
                          ? ` — ${ALASAN_ORIENTASI_TERKUNCI[form.ukuran_kertas]}.` : "."}
                        {" "}Ukuran kertas ini tidak bisa diubah orientasinya.
                      </span>
                    </div>
                  ) : (
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Orientasi Cetak</span>
                      <select value={form.orientasi || orientasiBawaan[form.ukuran_kertas] || "portrait"}
                        data-testid="ft-orientasi"
                        onChange={(e) => setForm((f) => ({ ...f, orientasi: e.target.value }))}
                        className={inp}>
                        {orientasiOptions.map((o) => (
                          <option key={o} value={o}>{ORIENTASI_LABEL[o] || o}</option>
                        ))}
                      </select>
                      {form.ukuran_kertas === "continuous_half" && (
                        <span className="text-[11px] text-slate-400" data-testid="ft-orientasi-continuous-hint">
                          Kertas continuous bisa dipasang mendatar maupun tegak di printer. Kalau hasil cetak
                          meleset dari kertas, coba ganti orientasi di sini dulu sebelum mengubah skala.
                        </span>
                      )}
                    </label>
                  )}

                  {/* Skala cetak / "fit to paper" (owner 30 Agu 2026 poin 4). */}
                  <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="ft-blok-skala">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-slate-600">Skala Cetak (fit to paper)</span>
                      <span className="text-xs font-mono text-slate-500" data-testid="ft-skala-nilai">
                        {form.fit_otomatis ? "otomatis" : `${form.skala_cetak}%`}
                      </span>
                    </div>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={!!form.fit_otomatis} data-testid="ft-fit-otomatis"
                        onChange={(e) => setForm((f) => ({ ...f, fit_otomatis: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-300 accent-emerald-600 mt-0.5" />
                      <span>
                        Paskan otomatis ke lebar kertas
                        <span className="block text-[11px] text-slate-500 mt-0.5">
                          Lebar dokumen diukur saat mencetak lalu dikecilkan secukupnya supaya tidak ada yang
                          terpotong. Hanya MENGECILKAN, tidak pernah membesarkan — memperbesar dokumen yang sudah
                          muat justru bisa mendorong isinya ke halaman kedua.
                        </span>
                      </span>
                    </label>
                    <div className={form.fit_otomatis ? "opacity-40 pointer-events-none" : ""}>
                      <input type="range" min={skalaMin} max={skalaMaks} step={5}
                        value={form.skala_cetak} data-testid="ft-skala-slider"
                        onChange={(e) => setForm((f) => ({ ...f, skala_cetak: Number(e.target.value) }))}
                        className="w-full accent-emerald-600" />
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {SKALA_PRESET.filter((s) => s >= skalaMin && s <= skalaMaks).map((s) => (
                          <button key={s} type="button" data-testid={`ft-skala-preset-${s}`}
                            onClick={() => setForm((f) => ({ ...f, skala_cetak: s }))}
                            className={`h-7 px-2 rounded-md border text-[11px] ${
                              Number(form.skala_cetak) === s
                                ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                            {s}%
                          </button>
                        ))}
                        <input type="number" min={skalaMin} max={skalaMaks} value={form.skala_cetak}
                          data-testid="ft-skala-angka"
                          onChange={(e) => setForm((f) => ({ ...f, skala_cetak: e.target.value }))}
                          className="h-7 w-20 px-2 rounded-md border border-slate-200 text-[11px] text-slate-700" />
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {skalaMin}–{skalaMaks}%. 100% = ukuran normal (sama persis seperti sebelum pengaturan ini
                        ada). Turunkan kalau isi faktur melebar keluar kertas continuous.
                      </p>
                    </div>
                  </div>

                  {belumAdaDataCetak(form.jenis) && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800"
                         data-testid="ft-catatan-retur">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        Modul retur belum ada sebagai transaksi di aplikasi ini — template ini disiapkan lebih dulu
                        supaya siap dipakai begitu modul retur tersedia.
                      </span>
                    </div>
                  )}

                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Nama Template *</span>
                    <input value={form.nama} data-testid="ft-nama"
                      onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))}
                      placeholder="Contoh: Faktur Penjualan Cabang Utama" className={inp} />
                  </label>

                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Cabang</span>
                    {cabangFreeText ? (
                      <input value={form.cabang} data-testid="ft-cabang"
                        onChange={(e) => setForm((f) => ({ ...f, cabang: e.target.value }))}
                        placeholder="Kosongkan = berlaku semua cabang" className={inp} />
                    ) : (
                      <select value={form.cabang} data-testid="ft-cabang"
                        onChange={(e) => setForm((f) => ({ ...f, cabang: e.target.value }))}
                        className={inp}>
                        <option value="">— Semua cabang —</option>
                        {cabangOpsi.map((c) => <option key={c} value={c}>{c}</option>)}
                        {form.cabang && !cabangOpsi.includes(form.cabang) && (
                          <option value={form.cabang}>{form.cabang} (lama)</option>
                        )}
                      </select>
                    )}
                    <span className="text-[11px] text-slate-400">Kosong = berlaku untuk semua cabang.</span>
                  </label>

                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Kop Dokumen</p>
                    <div className="space-y-3">
                      <input value={form.kop.judul} data-testid="ft-kop-judul"
                        onChange={(e) => setForm((f) => ({ ...f, kop: { ...f.kop, judul: e.target.value } }))}
                        placeholder="Judul (mis. FAKTUR PENJUALAN)" className={inp} />
                      <input value={form.kop.alamat} data-testid="ft-kop-alamat"
                        onChange={(e) => setForm((f) => ({ ...f, kop: { ...f.kop, alamat: e.target.value } }))}
                        placeholder="Alamat toko" className={inp} />
                      <input value={form.kop.telepon} data-testid="ft-kop-telepon"
                        onChange={(e) => setForm((f) => ({ ...f, kop: { ...f.kop, telepon: e.target.value } }))}
                        placeholder="Nomor telepon" className={inp} />
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.kop.tampil_logo} data-testid="ft-kop-logo"
                          onChange={(e) => setForm((f) => ({ ...f, kop: { ...f.kop, tampil_logo: e.target.checked } }))}
                          className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span className="text-slate-700">Tampilkan logo</span>
                      </label>
                      <div>
                        <div className="flex items-center gap-3">
                          {form.kop.logo_url ? (
                            <div className="relative shrink-0">
                              <img src={form.kop.logo_url} alt="Pratinjau logo" data-testid="ft-kop-logo-preview"
                                className="w-14 h-14 rounded-lg object-cover border border-slate-200 bg-white" />
                              <button type="button" onClick={hapusLogo} data-testid="ft-kop-logo-hapus"
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-slate-50 border border-dashed border-slate-300 grid place-items-center text-slate-300 shrink-0">
                              <ImageIcon className="w-5 h-5" />
                            </div>
                          )}
                          <label data-testid="ft-kop-logo-upload"
                            className={`h-9 px-3 rounded-lg border border-slate-200 text-xs inline-flex items-center gap-1.5 cursor-pointer ${compressingLogo ? "opacity-60 pointer-events-none" : "hover:bg-slate-50"}`}>
                            {compressingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {compressingLogo ? "Mengompres..." : "Unggah Logo"}
                            <input type="file" accept="image/*" className="hidden" data-testid="ft-kop-logo-input"
                              onChange={onPilihLogo} disabled={compressingLogo} />
                          </label>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5">
                          Foto langsung dari HP (bisa 5MB+) otomatis diperkecil &amp; dikompres di browser sebelum
                          disimpan — targetnya di bawah ±370KB. Format PNG/JPG/SVG, disarankan gambar persegi
                          dengan latar polos.
                        </p>
                      </div>
                    </div>
                  </div>

                  {form.jenis === "penjualan" && <PesanWaEditor value={form.pesan_wa || ""} onChange={value => setForm(f => ({...f, pesan_wa: value}))} csat={form.opsi.sertakan_link_csat} />}
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Footer</span>
                    <textarea value={form.footer_teks} data-testid="ft-footer" rows={2}
                      onChange={(e) => setForm((f) => ({ ...f, footer_teks: e.target.value }))}
                      placeholder="Contoh: Terima kasih atas pembelian Anda."
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                    <span className="text-[11px] text-slate-400">
                      Satu paragraf kecil, rata tengah di bagian paling bawah dokumen. BEDA dari "Catatan
                      Penjual/Kasir" dan "Syarat &amp; Ketentuan" di bawah — lihat penjelasan masing-masing.
                    </span>
                  </label>

                  <div className="border-t border-slate-100 pt-4 space-y-4">
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Catatan Penjual/Kasir</span>
                      <textarea value={form.catatan_penjual} data-testid="ft-catatan-penjual" rows={2}
                        onChange={(e) => setForm((f) => ({ ...f, catatan_penjual: e.target.value }))}
                        placeholder="Contoh: Barang yang sudah dibeli tidak dapat ditukar."
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                      <span className="text-[11px] text-slate-400">
                        Catatan TETAP milik TEMPLATE ini (mis. kebijakan garansi/tukar) — SELALU sama di setiap
                        dokumen yang dicetak. BEDA dengan opsi "Tampilkan catatan" di bawah, yang menampilkan/
                        menyembunyikan catatan milik TRANSAKSI itu sendiri (diketik kasir saat transaksi
                        berlangsung). Keduanya tampil berdampingan saat dicetak, tidak saling menimpa.
                      </span>
                    </label>

                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Syarat &amp; Ketentuan</span>
                      <textarea value={form.syarat_ketentuan} data-testid="ft-syarat-ketentuan" rows={3}
                        onChange={(e) => setForm((f) => ({ ...f, syarat_ketentuan: e.target.value }))}
                        placeholder={"Contoh:\n1. Garansi berlaku 7 hari untuk kerusakan pabrik.\n2. Barang yang sudah dibuka segel tidak dapat dikembalikan."}
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                      <span className="text-[11px] text-slate-400">
                        Blok multi-baris TERPISAH dari "Footer" di atas. Footer tetap satu paragraf kecil rata
                        tengah (mis. ucapan terima kasih) — Syarat &amp; Ketentuan ini untuk daftar ketentuan yang
                        lebih panjang (garansi, kebijakan retur, dsb).
                      </span>
                    </label>
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Opsi Tampilan</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ["tampil_sku", "Tampilkan SKU"],
                        ["tampil_imei", "Tampilkan IMEI"],
                        ["tampil_ttd", "Blok tanda tangan"],
                        ["tampil_catatan", "Tampilkan catatan"],
                        ["sertakan_link_csat", "Sertakan tautan CSAT"],
                        ["tampil_komponen_bundle", "Tampilkan komponen paket (bundle kit)"],
                      ].map(([k, l]) => (
                        <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={!!form.opsi[k]} data-testid={`ft-opsi-${k}`}
                            onChange={(e) => setForm((f) => ({ ...f, opsi: { ...f.opsi, [k]: e.target.checked } }))}
                            className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                          <span className="text-slate-700">{l}</span>
                        </label>
                      ))}
                    </div>
                    {form.opsi.sertakan_link_csat && (
                      <p className="flex items-start gap-1.5 text-[11px] text-slate-500 mt-2" data-testid="ft-opsi-csat-hint">
                        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                        QR penilaian pelayanan hanya BENAR-BENAR tercetak kalau tautan CSAT untuk transaksi itu
                        sudah pernah dibuat (lewat "Kirim Faktur via WhatsApp" atau "Buat Tautan CSAT") — mencentang
                        opsi ini saja TIDAK otomatis membuatnya muncul.
                      </p>
                    )}
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Pembatasan Gudang</p>
                    {!JENIS_TERIKAT_GUDANG.includes(form.jenis) && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-2"
                         data-testid="ft-gudang-tidak-relevan">
                        Jenis dokumen "{JENIS_LABEL[form.jenis] || form.jenis}" tidak terikat satu gudang tunggal —
                        pembatasan di bawah ini tidak berpengaruh untuk jenis ini, tapi tetap tersimpan untuk
                        jaga-jaga kalau template dipakai ulang untuk jenis lain.
                      </p>
                    )}
                    <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                      <input type="checkbox" checked={form.all_warehouses} data-testid="ft-all-warehouses"
                        onChange={(e) => setForm((f) => ({
                          ...f, all_warehouses: e.target.checked,
                          allowed_warehouse_ids: e.target.checked ? [] : f.allowed_warehouse_ids,
                        }))}
                        className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                      <span className="text-slate-700">Boleh dipakai semua gudang</span>
                    </label>
                    {!form.all_warehouses && (
                      <div>
                        {warehouseFreeText ? (
                          <>
                            <input value={(form.allowed_warehouse_ids || []).join(", ")} data-testid="ft-warehouse-freetext"
                              onChange={(e) => setForm((f) => ({
                                ...f, allowed_warehouse_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                              }))}
                              placeholder="Tempel id gudang, pisahkan koma" className={inp} />
                            <p className="text-[11px] text-amber-600 mt-1">
                              Daftar gudang tidak bisa dimuat (kemungkinan akun ini tidak punya izin melihat
                              gudang) — masukkan id gudang manual, salin dari menu Gudang.
                            </p>
                          </>
                        ) : warehouseOpsi.length === 0 ? (
                          <p className="text-xs text-slate-400 italic py-1">Belum ada gudang terdaftar.</p>
                        ) : (
                          <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100"
                               data-testid="ft-warehouse-list">
                            {warehouseOpsi.map((w) => {
                              const dicentang = (form.allowed_warehouse_ids || []).includes(w.id);
                              return (
                                <label key={w.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                                  <input type="checkbox" checked={dicentang} data-testid={`ft-warehouse-${w.id}`}
                                    onChange={(e) => setForm((f) => {
                                      const set = new Set(f.allowed_warehouse_ids || []);
                                      if (e.target.checked) set.add(w.id); else set.delete(w.id);
                                      return { ...f, allowed_warehouse_ids: Array.from(set) };
                                    })}
                                    className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                                  <span className="text-slate-700">{w.nama}</span>
                                  {w.cabang && <span className="text-[10px] text-slate-400">({w.cabang})</span>}
                                </label>
                              );
                            })}
                          </div>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">
                          Kalau tidak ada satu pun yang dicentang, template ini tidak akan bisa dipakai gudang mana
                          pun — pilih minimal satu gudang, atau centang "Boleh dipakai semua gudang" di atas.
                        </p>
                      </div>
                    )}
                  </div>

                  <label className="flex items-start gap-2 text-sm cursor-pointer bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <input type="checkbox" checked={!!form.is_default} data-testid="ft-is-default"
                      onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 accent-emerald-600 mt-0.5" />
                    <span>
                      Jadikan default untuk jenis &amp; cabang ini
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        Template default lama untuk kombinasi jenis + cabang yang sama akan otomatis dicabut.
                      </span>
                    </span>
                  </label>

                  <div className="flex gap-2 pt-2">
                    <button onClick={tutupForm} data-testid="ft-batal"
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
                      Batal
                    </button>
                    <button onClick={simpan} disabled={saving} data-testid="ft-simpan"
                      className="flex-1 h-10 rounded-lg bg-[#059669] hover:bg-[#047857] disabled:opacity-60 text-white text-sm font-medium">
                      {saving ? "Menyimpan..." : "Simpan Template"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Pratinjau hidup */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 no-print">
                  Pratinjau — data contoh, bukan transaksi sungguhan
                </p>
                {form.opsi.sertakan_link_csat && (
                  <div className="mb-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-800 no-print"
                       data-testid="ft-preview-csat-disclaimer">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Blok QR/tautan CSAT yang muncul di bawah ini adalah CONTOH STATIS untuk pratinjau. Pada
                      dokumen sungguhan, blok ini hanya tercetak kalau undangan CSAT untuk transaksi itu SUDAH
                      pernah dibuat (lewat "Kirim Faktur via WhatsApp" atau "Buat Tautan CSAT") — tidak otomatis
                      muncul hanya karena opsi ini dicentang.
                    </span>
                  </div>
                )}
                <KomponenPreview
                  data={dummyEntity}
                  template={form}
                  noFaktur="INV-CONTOH1"
                  csatLink={dummyCsat}
                  tampilkanTombolCetak
                />
              </div>
            </div>
          )}

          {loading ? (
            <LoadingState loading data-testid="ft-loading" />
          ) : (
            <div className="space-y-6">
              {grouped.map(({ jenis, list }) => (
                <section key={jenis} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={`ft-grup-${jenis}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="font-display text-base font-bold text-slate-900">{JENIS_LABEL[jenis] || jenis}</h2>
                    <span className="text-xs text-slate-400">{list.length} template</span>
                  </div>
                  {belumAdaDataCetak(jenis) && (
                    <p className="text-xs text-amber-700 mb-3">
                      Modul retur belum ada — template disiapkan untuk nanti.
                    </p>
                  )}
                  {list.length === 0 ? (
                    <p className="text-xs text-slate-400 italic py-3">Belum ada template untuk jenis ini.</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {list.map((item) => (
                        <div key={item.id} className="py-3 flex items-center justify-between gap-3 flex-wrap"
                             data-testid={`ft-row-${item.id}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-900 text-sm">{item.nama}</p>
                              {item.is_default && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full"
                                      data-testid={`ft-badge-default-${item.id}`}>
                                  Default
                                </span>
                              )}
                              <span className="text-[10px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                {item.cabang || "Semua Cabang"}
                              </span>
                              <span className="text-[10px] text-slate-400">{UKURAN_LABEL[item.ukuran_kertas] || item.ukuran_kertas}</span>
                              {/* Orientasi & skala ikut terlihat di daftar —
                                  kalau cetakan meleset di satu cabang, yang
                                  pertama dicari orang adalah "template mana
                                  yang skalanya beda", bukan membuka satu per satu. */}
                              <span className="text-[10px] text-slate-400" data-testid={`ft-orientasi-badge-${item.id}`}>
                                · {ORIENTASI_LABEL[item.orientasi] || item.orientasi || "Portrait (tegak)"}
                              </span>
                              {(item.fit_otomatis || (item.skala_cetak && item.skala_cetak !== SKALA_DEFAULT)) && (
                                <span className="text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full"
                                      data-testid={`ft-skala-badge-${item.id}`}>
                                  {item.fit_otomatis ? "Skala otomatis" : `Skala ${item.skala_cetak}%`}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {canUpdate && !item.is_default && (
                              <button onClick={() => jadikanDefault(item)} data-testid={`ft-default-${item.id}`}
                                className="h-8 px-2.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1">
                                <Star className="w-3.5 h-3.5" /> Jadikan Default
                              </button>
                            )}
                            {canUpdate && (
                              <button onClick={() => openEdit(item)} data-testid={`ft-edit-${item.id}`}
                                className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 inline-grid place-items-center">
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button onClick={() => hapus(item)} data-testid={`ft-hapus-${item.id}`}
                                className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-grid place-items-center">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
